import {
  DEFAULT_DATASET_TIME_INTERVAL_MINUTES,
  getAggregatedDatasets,
  getShortestDatasetWithIntervalInMinutes,
  MIN_DATASET_TIME_INTERVAL_MINUTES,
  serializeTableAccessor,
} from 'helpers/dataset.helper/dataset.helper'
import { addMinutes, formatHHmm } from 'helpers/dateTime.helper/dateTime.helper'
import { clone, isNumber, round } from 'helpers/global.helper/global.helper'
import { TimeSeriesDataTableData } from 'ui/components/TimeSeriesDataTable/types'
import Datetime from 'utils/datetime/datetime'
import { calcMean } from 'utils/numberUtils/numberUtils'
import { DatasetTableItem } from './DatasetTable.types'

export function getHourlyDataForTable(
  datasetsInTable: Dataset[],
  itemsToShowInTable: DatasetTableItem[],
  currentOptimizationHour: ISODateTime,
  show_only_value_in_future: boolean
): { data: TimeSeriesDataTableData[]; canExpandRows: boolean } {
  const data: TimeSeriesDataTableData[] = []
  datasetsInTable.forEach((dataset) => {
    const serializedTableAccessor = serializeTableAccessor(dataset.return_id)
    const item = itemsToShowInTable.find((item) => item.data_id === dataset.return_id)
    const indexToDisappear: number[] = []
    dataset.times.forEach((time, index) => {
      if (!data[index]) {
        data[index] = { time: '' }
      }
      if (!data[index][`time`]) {
        data[index][`time`] = Datetime.toLocalTime(time as ISODateTime, 'longDayTextWithoutYear')
      }
    })

    dataset.values?.forEach((value, index) => {
      if (!indexToDisappear.includes(index)) {
        data[index][serializedTableAccessor] = isNumber(value) ? round(value, item?.decimals ?? 1) : null
      }
    })
  })

  if (show_only_value_in_future) {
    const now = currentOptimizationHour
    const nowInLocalTime = Datetime.toLocalTime(now, 'longDayTextWithoutYear')
    const nowIndex = data.findIndex((item) => item[`time`] === nowInLocalTime)
    return { data: data.splice(nowIndex, data.length), canExpandRows: false }
  }

  return { data, canExpandRows: false }
}

type DatasetHourLookup = {
  dataset: Dataset
  valueByTime: Map<string, number | null>
  valuesByHour: Map<string, (number | null)[]>
}

// Keyed by hour so a dataset coarser than the table's finest resolution (e.g. hourly in a quarterly
// table) contributes its own hour value, not a positional average of the finest dataset's rows.
function buildHourAlignedDatasetLookups(datasets: Dataset[]): DatasetHourLookup[] {
  return datasets.map((dataset) => {
    const valueByTime = new Map<string, number | null>()
    const valuesByHour = new Map<string, (number | null)[]>()
    dataset.times.forEach((time, index) => {
      const value = dataset.values?.[index] ?? null
      const datetime = new Datetime(time)
      valueByTime.set(datetime.ISOString, value)
      const hourBucket = valuesByHour.get(datetime.ISOString00Minutes)
      if (hourBucket) {
        hourBucket.push(value)
      } else {
        valuesByHour.set(datetime.ISOString00Minutes, [value])
      }
    })
    return { dataset, valueByTime, valuesByHour }
  })
}

export function getQuarterlyDataForTable(
  datasetsInTable: Dataset[],
  itemsToShowInTable: DatasetTableItem[],
  currentOptimizationHour: ISODateTime,
  show_only_value_in_future: boolean
): { data: TimeSeriesDataTableData[]; canExpandRows: boolean } {
  if (!datasetsInTable || datasetsInTable.length === 0) {
    return { data: [], canExpandRows: false }
  }

  // Time resolution: group by hour using the dataset that has the finest resolution
  const { interval: shortestInterval, dataset: datasetWithFinestResolution } =
    getShortestDatasetWithIntervalInMinutes(datasetsInTable)
  const canExpandRows = shortestInterval !== DEFAULT_DATASET_TIME_INTERVAL_MINUTES
  const hourGroups: Record<string, { times: string[]; firstTime: string }> = {}
  const times = datasetWithFinestResolution?.times || []
  times.forEach((time) => {
    // Group all times by their hour
    const hourKey = new Datetime(time).ISOString00Minutes
    if (!hourGroups[hourKey]) {
      hourGroups[hourKey] = { times: [], firstTime: hourKey }
    }
    hourGroups[hourKey].times.push(time as ISODateTime)
  })

  const datasetLookups = buildHourAlignedDatasetLookups(datasetsInTable)

  const data: TimeSeriesDataTableData[] = []
  Object.values(hourGroups).forEach(({ times, firstTime }) => {
    const hourRow: TimeSeriesDataTableData = {
      time: Datetime.toLocalTime(firstTime as ISODateTime, 'longDayTextWithoutYear'),
      isoTime: firstTime,
      subRows: [],
    }

    const subRows: TimeSeriesDataTableData[] = []
    // Only expand subRows if the finest resolution is 15 minutes
    if (shortestInterval !== DEFAULT_DATASET_TIME_INTERVAL_MINUTES) {
      times.forEach((time) => {
        const start = formatHHmm(time)
        const end = formatHHmm(addMinutes(time, shortestInterval ?? MIN_DATASET_TIME_INTERVAL_MINUTES))
        const quarterRow: TimeSeriesDataTableData = {
          time: `${start}-${end}`,
          isoTime: time,
        }
        subRows.push(quarterRow)
      })
    }

    hourRow.subRows = subRows

    // Set values for datasets
    datasetLookups.forEach(({ dataset, valueByTime, valuesByHour }) => {
      const accessor = serializeTableAccessor(dataset.return_id)
      const item = itemsToShowInTable.find((item) => item.data_id === dataset.return_id)
      const hourValues = valuesByHour.get(firstTime) ?? []
      const nonEmptyValue = hourValues.find((v) => v != null)
      const nonNullValues = hourValues.filter((v): v is number => v != null)
      const avg = nonNullValues.length === 0 ? null : calcMean(nonNullValues)
      hourRow[accessor] = isNumber(avg) ? round(avg, item?.decimals ?? 1) : null

      subRows.forEach((subRow, subRowIndex) => {
        const exactValue = valueByTime.get(new Datetime(times[subRowIndex]).ISOString)
        const val = exactValue ?? nonEmptyValue
        subRow[accessor] = isNumber(val) ? round(val, item?.decimals ?? 1) : null
      })
    })

    data.push(hourRow)
  })

  if (show_only_value_in_future) {
    // Use Datetime utility for hour comparison
    const getHourKey = (t: string | number) => {
      const dateObj = typeof t === 'number' ? new Date(t) : new Date(t)
      dateObj.setMinutes(0, 0, 0)
      return Datetime.toLocalTime(dateObj.toISOString() as ISODateTime, 'shortDayWithHour')
    }
    const nowHour = getHourKey(currentOptimizationHour)
    const startIndex = data.findIndex((row) => row.isoTime !== undefined && getHourKey(row.isoTime) >= nowHour)
    return { data: data.slice(startIndex), canExpandRows }
  }

  return { data, canExpandRows }
}

// Reduces sub-hourly datasets to one value per hour (the mean of that hour's non-null
// values), matching the collapsed hour rows produced by getQuarterlyDataForTable. Used so
// footer aggregations summarise the hourly values instead of every quarter. Hourly datasets
// are returned unchanged, since each hour already holds a single value.
export function reduceDatasetsToHourly(datasets: Dataset[]): Dataset[] {
  return datasets.map((dataset) => {
    const hourGroups: Record<string, (number | null)[]> = {}
    const hourKeysInOrder: ISODateTime[] = []
    dataset.times.forEach((time, index) => {
      const hourKey = new Datetime(time).ISOString00Minutes
      if (!hourGroups[hourKey]) {
        hourGroups[hourKey] = []
        hourKeysInOrder.push(hourKey)
      }
      hourGroups[hourKey].push(dataset.values?.[index] ?? null)
    })

    const times: ISODateTime[] = []
    const values: (number | null)[] = []
    hourKeysInOrder.forEach((hourKey) => {
      const nonNullValues = hourGroups[hourKey].filter((value): value is number => isNumber(value))
      times.push(hourKey)
      values.push(nonNullValues.length === 0 ? null : calcMean(nonNullValues))
    })

    return { ...dataset, times, values }
  })
}

// Footer Sum/Average/Min/Max rows. When hours are expandable the collapsed hour rows show the mean
// of their quarters, so aggregations summarise those hourly values rather than every quarter.
export function getFooterAggregationRows(
  datasets: Dataset[],
  expandableHours: boolean,
  hideSumAndAverage: boolean,
  hideMinAndMax: boolean
): { sumAndAverageRows: Record<string, unknown>[]; minAndMaxRows: Record<string, unknown>[] } {
  const source = expandableHours ? reduceDatasetsToHourly(datasets) : datasets
  const sumAndAverageRows = hideSumAndAverage
    ? []
    : [...getAggregatedDatasets(source, 'Sum'), ...getAggregatedDatasets(source, 'Average')]
  const minAndMaxRows = hideMinAndMax
    ? []
    : [...getAggregatedDatasets(source, 'Max'), ...getAggregatedDatasets(source, 'Min')]
  return { sumAndAverageRows, minAndMaxRows }
}

function isZeroOrNull(currentValue: number | null): boolean {
  return currentValue === 0 || currentValue === null
}

// Applies decimal conversion to dataset values based on item configuration.
export function applyDecimalConversion(datasets: Dataset[], items: DatasetTableItem[]): Dataset[] {
  const itemsMap = new Map(items.map((item) => [item.data_id, item]))

  return datasets.map((dataset) => {
    const item = itemsMap.get(dataset.return_id)
    if (item && typeof item.decimals === 'number') {
      return {
        ...dataset,
        values: dataset.values.map((value) => {
          if (value === null || value === undefined) return value
          return Number(value.toFixed(item.decimals))
        }),
      }
    }
    return { ...dataset }
  })
}

/**
 * Cleans and filters datasets for table display.
 *
 * Processing order:
 * 1. Convert values to correct number of decimals based on items configuration
 * 2. Filter out single-value datasets if hideSingleValueDatasets is true
 * 3. Filter out datasets with all zero/null values if do_not_show_zero_or_null_values_in_table is true
 */
export function cleanDatasetsForTable(
  datasets: Dataset[],
  items: DatasetTableItem[],
  do_not_show_zero_or_null_values_in_table: boolean,
  hideSingleValueDatasets: boolean
): Dataset[] {
  let filteredDatasets = clone(datasets)

  // Convert values to correct number of decimals based on items configuration
  filteredDatasets = applyDecimalConversion(filteredDatasets, items)

  if (hideSingleValueDatasets) {
    filteredDatasets = filteredDatasets.filter((dataset) => !(dataset.times.length <= 1)) //datasets with only one value does not show in dataset table
  }

  if (do_not_show_zero_or_null_values_in_table) {
    filteredDatasets = filteredDatasets.filter((dataset) => !dataset.values.every(isZeroOrNull)) //filter datasets on values if all of them are 0 or null
  }

  return filteredDatasets
}
